import Order from '../models/Order';
import Payment from '../models/Payment';
import { PaymentStatus } from '../models/PaymentStatus';
import Venue from '../models/Venue';
import { environment } from '../../environments/environment';
import { isMongoId } from 'validator';
import { PaymentMethod } from '../enums/PaymentMethod';
import PaySession from '../models/PaySession';
import { CheckoutArticleGroup } from '../models/CheckoutArticleGroup';
import { PaySessionSplitType } from '../enums/PaySessionSplitType';
import { PaySessionStatistics } from '../models/PaySessionStatistics';
import { PosCheckoutTaxes } from '../models/PosCheckoutTaxes';
import { TipSelectorValue, TipType } from '../../app/components/tip-selector/tip-selector.component';

// export const phoneCountries: PhoneCountry[] = [
// 	{
// 		code: 'de',
// 		tel: '+49'
// 	},
// 	{
// 		code: 'au',
// 		tel: '+43'
// 	}
// ].filter(cc => environment.countryList.indexOf(cc.code) >= 0);

export const sanitizeId = (id: string, isIncludeCustomerGroup: boolean = false) => {
	if (!id) {
		return id;
	}
	if (!id.startsWith(environment.customerGroup + '_') && !isMongoId(id) && !isIncludeCustomerGroup) {
		return environment.customerGroup + '_' + id;
	}
	return id;
};

export const sleep = (ms: number) => {
	return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Deep clones an object with optimal performance.
 * Uses structuredClone() for modern browsers, falls back to a JSON method for older browsers.
 * @param obj - The object to deep clone
 * @returns A deep clone of the object
 */
export const deepClone = <T>(obj: T): T => {
	if (!('structuredClone' in globalThis)) {
		// Fallback to JSON method for older browsers
		return JSON.parse(JSON.stringify(obj));
	} else {
		try {
			return structuredClone(obj);
		} catch (e) {
			console.error('Error while cloning object', e);
			return JSON.parse(JSON.stringify(obj));
		}
	}
};

export const paymentFromOrder = (order: Order, tip: number = 0): Partial<Payment> => {
	return {
		orders: [order._id],
		status: PaymentStatus.created,
		currency: order.currency,
		sum: numberD(order.totalPrice),
		tip: tip,
	};
};

export const numberToCurrency = (price: any, currencyCode?: string): string => {
	if (!currencyCode) {
		return '' + numberD(price);
	}
	return numberD(price)
		.toLocaleString(navigator.language, {
			style: 'currency',
			currency: currencyCode,
		})
		.replace('.', ',');
};

export const last = <T>(arr: T[]): T | null => {
	if (arr.length) {
		return arr[arr.length - 1];
	} else {
		return null;
	}
};
export const numberD = (x: any): number => {
	if (x === undefined) {
		return 0;
	}
	if (x === null) {
		return 0;
	}
	let candidate;
	if (x.$numberDecimal !== undefined) {
		candidate = +x.$numberDecimal;
	} else {
		candidate = +x;
	}
	if (isNaN(candidate)) {
		return 0;
	}
	return candidate;
};

export const paymentFromOrders = (orders: Order[], tip: number = 0): Partial<Payment> => {
	return {
		orders: orders.map(it => it._id),
		status: PaymentStatus.created,
		currency: orders[0].currency,
		tip: tip,
	};
};

export const paymentMethodsFromVenue = (venue: Venue): PaymentMethod[] => {
	const paymentMethods: PaymentMethod[] = [];

	if (venue?.adyenEnabled) {
		paymentMethods.push(PaymentMethod.ADYEN);
	}
	// @ts-ignore
	const applePayAvailable = !!window.ApplePaySession;
	if (venue.gpEnabled) {
		paymentMethods.push(PaymentMethod.GPAY);
	}
	if (venue.apEnabled && applePayAvailable) {
		paymentMethods.push(PaymentMethod.APAY);
	}

	if (venue.sbEnabled) {
		paymentMethods.push(PaymentMethod.SOFORTBANKING);
	}

	if (venue.adyenPaypalEnabled) {
		paymentMethods.push(PaymentMethod.PAYPAL_ADYEN);
	} else if (venue.ppEnabled) {
		paymentMethods.push(PaymentMethod.PAYPAL);
	}
	if (venue.cashEnabled) {
		paymentMethods.push(PaymentMethod.CASH);
	}
	if (venue.piggyGiftCardPaymentEnabled) {
		paymentMethods.push(PaymentMethod.PIGGY_GIFT_CARD);
	}
	return paymentMethods;
};

export function priceOfCheckoutOrderedArticle(checkoutOrderedArticle: Partial<CheckoutArticleGroup>): number {
	return (
		(numberD(checkoutOrderedArticle.price) ?? 0) +
		(checkoutOrderedArticle.modifiers?.reduce((prev, curr) => {
			return prev + numberD(curr.price);
		}, 0) ?? 0)
	);
}

export function getPaySessionStatistics(
	paySession: PaySession,
	generatedOrder: Order | null = null,
	selectedTips: TipSelectorValue | null = null
): PaySessionStatistics {
	let totalPrice = 0;
	let selectedProductsTotalPrice = 0;
	let totalProducts = 0;
	let selectedProducts = [] as Partial<CheckoutArticleGroup>[];
	let notSelectedAndNotPaidProducts = [] as Partial<CheckoutArticleGroup>[];
	let paidProducts = [] as Partial<CheckoutArticleGroup>[];
	let paidTotalPrice = 0;
	let notPaidProducts = [] as Partial<CheckoutArticleGroup>[];
	let combinedPosCheckoutTaxes: PosCheckoutTaxes[] = [];
	let totalDiscount = 0;
	let selectedDiscount = 0;
	paySession.checks.forEach(check => {
		check.posCheckoutTaxes
			?.map(tax => {
				return {
					taxRateId: tax.taxRateId,
					total: numberD(tax.total),
					name: tax.name,
				};
			})
			.forEach(tax => {
				const insertedIndex = combinedPosCheckoutTaxes.findIndex(it => it.taxRateId === tax.taxRateId);
				if (insertedIndex < 0) {
					combinedPosCheckoutTaxes.push(tax);
					return;
				}
				combinedPosCheckoutTaxes[insertedIndex].total += numberD(tax.total);
			});
		paidTotalPrice += numberD(check.alreadyPaidAmount);
		check.checkoutOrderedArticles.forEach(article => {
			if (article.participant === paySession.participant && !article.paid) {
				selectedProducts.push(article);
				selectedProductsTotalPrice = selectedProductsTotalPrice + priceOfCheckoutOrderedArticle(article);
				selectedDiscount += numberD(article.appliedDiscount ?? '0');
			}
			if (!article.participant && !article.paid) {
				notSelectedAndNotPaidProducts.push(article);
			}
			if (article.paid) {
				paidProducts.push(article);
			} else {
				notPaidProducts.push(article);
			}
			totalProducts = totalProducts + 1;
			totalPrice = totalPrice + priceOfCheckoutOrderedArticle(article);
			totalDiscount = totalDiscount + numberD(article.appliedDiscount ?? '0');
		});
	});

	const paidPayments = paySession.payments.filter(it => it.paid).length;
	const hasPaidPayments = paidPayments > 0;
	let selectedTotalPrice = 0;
	if (generatedOrder && !!generatedOrder.totalPrice) {
		selectedTotalPrice = +(generatedOrder.totalPrice ?? 0);
	} else {
		switch (paySession.splitType) {
			case PaySessionSplitType.FULL:
				selectedTotalPrice = paySession.topayAmount;
				break;
			case PaySessionSplitType.SEPARATE:
				selectedTotalPrice = +selectedProductsTotalPrice.toFixed(2);
				break;
			case PaySessionSplitType.EQUAL:
				selectedTotalPrice = +(paySession.topayAmount / Math.max((paySession.guestCount ?? 1) - paidPayments, 1)).toFixed(2);
				break;
		}
	}
	const tipsValue = selectedTips ? getAbsolutValueFromTipSelection(selectedTotalPrice, selectedTips!!) : 0;
	selectedTotalPrice = selectedTotalPrice + tipsValue;
	totalPrice = totalPrice + tipsValue;
	return {
		totalPrice: +totalPrice.toFixed(2),
		selectedTotalPrice,
		totalProducts,
		selectedProducts,
		selectedProductsCount: paySession.splitType === PaySessionSplitType.FULL ? notPaidProducts.length : selectedProducts.length,
		checkIds: paySession.checks.map(check => check.externalOrderId?.split('_')[1] ?? ''),
		table: paySession.tableNumber,
		paidProducts,
		paidProductsCount: paidProducts.length,
		paidTotalPrice: +paidTotalPrice.toFixed(2),
		guestCount: paySession.guestCount ?? 0,
		totalDue: +(totalPrice - paidTotalPrice).toFixed(2),
		donePaymentsCount: paidPayments,
		allowedToChangeSplitType: !hasPaidPayments,
		totalTax: paySession.totalTax,
		selectedTax: generatedOrder?.posCheckoutTaxes || [],
		combinedPosCheckoutTaxes,
		selectedDiscount,
		totalDiscount,
		hasSelectableItems: notSelectedAndNotPaidProducts.length > 0,
		tipsValue,
	};
}

export function getAbsolutValueFromTipSelection(totalPrice: number, tip: TipSelectorValue): number {
	switch (tip.type) {
		case TipType.RELATIVE:
			return +(totalPrice * (tip.value / 100)).toFixed(2);
		case TipType.ABSOLUTE:
			return tip.value;
	}
}

export function tipText(currencyFormat: Intl.NumberFormat, tip: TipSelectorValue | null): string {
	switch (tip?.type) {
		case TipType.RELATIVE:
			return `${tip.value}%`;
		case TipType.ABSOLUTE:
			return currencyFormat.format(tip.value);
		case undefined:
		case null:
			return '€?';
	}
}

export enum ArticleType {
	GAMEPLAY,
	MEMBERSHIP,
	FNB,
}

export function getArticleType(sku?: string): ArticleType {
	if (sku?.startsWith('401') || sku?.startsWith('402')) {
		return ArticleType.GAMEPLAY;
	}
	if (sku?.startsWith('701')) {
		return ArticleType.MEMBERSHIP;
	}
	if (sku === '130000003') {
		return ArticleType.GAMEPLAY;
	}

	return ArticleType.FNB;
}

export function getIconByArticleType(articleType: ArticleType): string {
	switch (articleType) {
		case ArticleType.GAMEPLAY:
			return '/assets/icon/gameplay.svg';
		case ArticleType.MEMBERSHIP:
			return '/assets/icon/membership.svg';
		default:
			return '/assets/icon/fnb.svg';
	}
}
